/** API Access — drive Wheeler PMT from a terminal, a script, Power BI, another application
 * or Claude.
 *
 * Modelled on Wheeler Change Control's page (Brian, 2026-10-29: where CAB has a feature, PMT
 * gets the same one): personal keys with scopes, expiry and a network; a Claude session; the
 * application guide; and how to stay safe. Everything factual is read from the running
 * service — `/meta`, the scope table, the network check — so it cannot describe an
 * environment other than the one you are looking at.
 */

import { useEffect, useState } from 'react'
import {
  AlertTriangle,
  Bot,
  Check,
  Copy,
  ExternalLink,
  KeyRound,
  Network as NetworkIcon,
  ShieldCheck,
  Workflow,
} from 'lucide-react'

import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  SectionHeader,
  inputClass,
} from '@/components/ui'
import { useMeta } from '@/features/administration/useAdmin'
import {
  type ApiKey,
  type ApiKeyCreated,
  type AssistantSession,
  useAllKeys,
  useCreateKey,
  useMyKeys,
  useNetwork,
  useRevokeKey,
  useScopes,
  useServicePrincipalGuide,
  useStartSession,
} from '@/features/api-access/useApiAccess'
import { Permissions, usePermission } from '@/lib/session'

// --- small pieces ---------------------------------------------------------------------

function Snippet({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="relative">
      {label ? <div className="micro-label mb-1 text-fg-muted">{label}</div> : null}
      <pre className="card overflow-x-auto whitespace-pre p-3 pr-16 text-xs leading-relaxed text-fg">
        {code}
      </pre>
      <button
        type="button"
        className="absolute right-2 rounded border border-border bg-surface px-2 py-1 text-[11px] text-fg-muted hover:text-fg"
        style={{ top: label ? 24 : 8 }}
        onClick={() => {
          void navigator.clipboard.writeText(code).then(() => {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          })
        }}
      >
        {copied ? (
          <>
            <Check size={11} aria-hidden className="inline" /> Copied
          </>
        ) : (
          <>
            <Copy size={11} aria-hidden className="inline" /> Copy
          </>
        )}
      </button>
    </div>
  )
}

function Banner({
  tone,
  title,
  children,
}: {
  tone: 'amber' | 'green' | 'info'
  title: string
  children: React.ReactNode
}) {
  const color =
    tone === 'amber' ? 'var(--status-amber)' : tone === 'green' ? 'var(--status-green)' : 'var(--status-info)'
  return (
    <div className="card mb-6 border-l-[3px] p-4" style={{ borderLeftColor: color }}>
      <p className="text-sm font-semibold text-fg">{title}</p>
      <div className="mt-1 text-sm text-fg-muted">{children}</div>
    </div>
  )
}

function Dialog({
  label,
  onClose,
  children,
}: {
  label: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </div>
    </div>
  )
}

function when(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function useCountdown(expiresAt: string | undefined): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!expiresAt) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [expiresAt])
  return expiresAt ? Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 1000)) : 0
}

// --- examples, generated from the live base URL ---------------------------------------------

function examples(base: string, token: string) {
  const host = new URL(base).origin
  return {
    env: `# ~/.zshrc, ~/.bashrc, your PowerShell profile, or a .env that git ignores — never a committed file
export PMT_API='${base}'
export PMT_API_KEY='${token}'`,
    curl: `# Who does this key act as, and what can it do?
curl -s -H "X-API-Key: $PMT_API_KEY" "$PMT_API/api/v1/me"

# Every project, as flat rows (page with nextCursor)
curl -s -H "X-API-Key: $PMT_API_KEY" "$PMT_API/api/v1/feed/projects?limit=500"

# The WIG board
curl -s -H "X-API-Key: $PMT_API_KEY" "$PMT_API/api/v1/wigs"`,
    python: `import os

import httpx

# Read the key from the environment. Never inline it into a file you might commit.
pmt = httpx.Client(base_url=os.environ["PMT_API"], headers={"X-API-Key": os.environ["PMT_API_KEY"]})


def feed(name: str, **params):
    """Every row of a feed, following nextCursor."""
    cursor = None
    while True:
        page = pmt.get(f"/api/v1/feed/{name}", params={**params, "cursor": cursor, "limit": 2000}).json()
        yield from page["items"]
        if not (cursor := page["nextCursor"]):
            return


projects = list(feed("projects"))
work_items = list(feed("work-items"))`,
    powerbi: `// Power BI / Excel: Get Data → Blank query → Advanced editor.
// Set the data source's privacy and credential to Anonymous: the key travels in the header.
// Scheduled refresh runs in Microsoft's cloud, so the key must allow "anywhere"
// (or refresh through an on-premises data gateway on the corporate network).
let
    Key = "${token}",
    Fetch = (feed as text, cursor as nullable text) =>
        Json.Document(
            Web.Contents(
                "${host}",
                [
                    RelativePath = "api/v1/feed/" & feed,
                    Headers = [#"X-API-Key" = Key],
                    Query = if cursor = null then [limit = "2000"] else [limit = "2000", cursor = cursor]
                ]
            )
        ),
    Pages = List.Generate(
        () => Fetch("projects", null),
        each _ <> null,
        each if _[nextCursor] = null then null else Fetch("projects", _[nextCursor]),
        each _[items]
    ),
    Projects = Table.FromRecords(List.Combine(Pages))
in
    Projects`,
  }
}

// --- the page ----------------------------------------------------------------------------

export function ApiAccessPage() {
  const { data: meta, isPending, error } = useMeta()
  const isAdmin = usePermission(Permissions.adminClientsEdit)

  if (isPending) return <Loading label="Reading this deployment's configuration" />
  if (error) return <ErrorState error={error} />

  const api = meta?.api
  // The web domain's /api route reaches the API (Static Web Apps linked backend). The
  // service says which address to publish; locally it is this origin, proxied by Vite.
  const base = (api?.publicBaseUrl || window.location.origin).replace(/\/$/, '')
  const production = meta?.environment === 'production'

  return (
    <div className="mx-auto max-w-[1000px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">API Access</h1>
        <p className="mt-1 max-w-[75ch] text-sm text-fg-muted">
          Drive Wheeler PMT from a terminal, a script, Power BI, another application or Claude.
          Environment <strong className="text-fg">{meta?.environment}</strong> · version{' '}
          {meta?.version}.
        </p>
      </header>

      {production ? (
        <Banner tone="amber" title="Building or testing an integration? Use dev.">
          This is production — the real portfolio. Changes made through the API are real and are
          audited under your name. Build against the dev environment and come back here when it
          works. You will need a separate key — keys do not work across environments.
        </Banner>
      ) : (
        <Banner tone="green" title="This is dev — the right place to build.">
          Separate database from production. Experiment freely. Keys made here do not work against
          production.
        </Banner>
      )}

      <NetworkStatus />

      <div className="mb-8 grid gap-4 md:grid-cols-2">
        <ClaudeCard />
        <ApplicationCard />
      </div>

      <HowKeysWork />
      <CreateKey base={base} />
      <MyKeys />
      {isAdmin ? <AllKeys /> : null}
      <Reference base={base} docsPath={api?.docsPath} openapiPath={api?.openapiPath} audience={api?.audience} />
    </div>
  )
}

// --- network --------------------------------------------------------------------------------

function NetworkStatus() {
  const { data } = useNetwork()
  if (!data) return null
  return (
    <div className="mb-6 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
      <NetworkIcon size={15} aria-hidden />
      PMT sees you calling from <code className="text-fg">{data.ip || 'an unknown address'}</code>
      {data.onCorporateNetwork ? (
        <Pill tone="green">corporate network</Pill>
      ) : (
        <Pill tone="amber">not the corporate network</Pill>
      )}
      <span className="text-xs">
        Keys pinned to the corporate network work only from there.
      </span>
      {data.forwardedFor ? (
        <span className="text-xs">· forwarding chain {data.forwardedFor.join(' → ')}</span>
      ) : null}
    </div>
  )
}

// --- Claude ---------------------------------------------------------------------------------

function ClaudeCard() {
  const start = useStartSession()
  const [session, setSession] = useState<AssistantSession | null>(null)
  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center gap-2">
        <Bot size={18} aria-hidden />
        <h2 className="font-heading text-lg text-fg">Work with PMT in Claude</h2>
      </div>
      <p className="mb-3 text-sm text-fg-muted">
        You get a prompt to paste into Claude with a key inside that expires in{' '}
        <strong className="text-fg">20 minutes</strong> — short enough that it never needs saving.
        Claude can read the portfolio and help update work items; it{' '}
        <strong className="text-fg">cannot delete, move a phase gate, create or close a project,
        or touch administration</strong>, and it asks you before any change.
      </p>
      <p className="mb-4 text-xs text-fg-muted">
        Nothing to set up: the prompt tells Claude to fetch the current API briefing itself.
        Pressing again ends the previous session.
      </p>
      <Button
        variant="primary"
        disabled={start.isPending}
        onClick={() => start.mutate(20, { onSuccess: setSession })}
      >
        <Bot size={14} aria-hidden /> {start.isPending ? 'Starting…' : 'Start a Claude session'}
      </Button>
      {start.error ? <ErrorState error={start.error} /> : null}
      {session ? <SessionDialog session={session} onClose={() => setSession(null)} /> : null}
    </div>
  )
}

function SessionDialog({ session, onClose }: { session: AssistantSession; onClose: () => void }) {
  const remaining = useCountdown(session.expiresAt)
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const minutes = Math.floor(remaining / 60)
  const seconds = String(remaining % 60).padStart(2, '0')
  return (
    <Dialog label="Paste this to Claude" onClose={() => (copied ? onClose() : setConfirming(true))}>
      <h2 className="mb-1 font-heading text-2xl text-fg">Paste this to Claude</h2>
      <p className="mb-4 text-sm text-fg-muted">
        It contains a live key, so it is shown once and only here. Everything Claude needs is in the
        text — it fetches the rest of the API briefing itself.
      </p>
      <pre className="card mb-3 max-h-80 overflow-auto whitespace-pre-wrap p-3 text-xs text-fg">
        {session.prompt}
      </pre>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <span>Scopes:</span>
        {session.scopes.map((scope) => (
          <Pill key={scope}>{scope}</Pill>
        ))}
        <span>· no deleting, no phase gates, no administration, no key creation</span>
      </div>
      <p className="mb-4 text-sm text-fg">
        {remaining > 0 ? (
          <>
            Expires in {minutes}:{seconds}
          </>
        ) : (
          <>Nothing here works any more. Start a session again for a fresh prompt.</>
        )}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {confirming ? (
          <p className="text-sm" style={{ color: 'var(--status-amber)' }}>
            You have not copied it. Close anyway?
          </p>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() => {
              void navigator.clipboard.writeText(session.prompt).then(() => {
                setCopied(true)
                setConfirming(false)
              })
            }}
          >
            <Copy size={14} aria-hidden /> {copied ? 'Copied' : 'Copy prompt'}
          </Button>
          <Button onClick={() => (copied || confirming ? onClose() : setConfirming(true))}>
            {confirming ? 'Close anyway' : 'Close'}
          </Button>
        </div>
      </div>
      <p className="mt-4 text-xs text-fg-muted">
        When the key expires, calls made with it simply start failing. If that happens mid-task,
        start again and give Claude the new prompt.
      </p>
    </Dialog>
  )
}

// --- applications -----------------------------------------------------------------------

function ApplicationCard() {
  const [open, setOpen] = useState(false)
  const guide = useServicePrincipalGuide(open)
  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center gap-2">
        <Workflow size={18} aria-hidden />
        <h2 className="font-heading text-lg text-fg">Build an app that reads PMT</h2>
      </div>
      <p className="mb-3 text-sm text-fg-muted">
        For something that runs on its own — a website, a pipeline, a service. You get a prompt
        that walks your Claude through connecting as an Entra{' '}
        <strong className="text-fg">service principal</strong>: the application brings its own
        identity from Azure, so no PMT key ever lives in your codebase. Applications are
        read-only.
      </p>
      <p className="mb-4 text-xs text-fg-muted">
        Nothing is minted here — this hands over guidance, not a credential. The role assignment
        needs an administrator; the prompt says which steps to hand over.
      </p>
      <Button onClick={() => setOpen(true)}>
        <Workflow size={14} aria-hidden /> Connecting an application
      </Button>
      {open ? (
        <Dialog label="Connecting an application" onClose={() => setOpen(false)}>
          <h2 className="mb-1 font-heading text-2xl text-fg">Connecting an application</h2>
          <p className="mb-4 text-sm text-fg-muted">
            Paste this to the Claude building your application. There is no credential in it, so
            close it whenever you like.
          </p>
          {guide.isPending ? <Loading label="Checking what this environment accepts" /> : null}
          {guide.error ? <ErrorState error={guide.error} /> : null}
          {guide.data ? (
            <>
              {!guide.data.spAuthEnabled ? (
                <Banner tone="amber" title="Not switched on here yet">
                  No application is listed on this environment yet, so a correctly issued token
                  still comes back 401 — that is configuration, not your code. Build against the
                  guidance and ask an administrator in parallel.
                </Banner>
              ) : null}
              <Snippet code={guide.data.prompt} label="The prompt to hand your Claude" />
              <dl className="mt-4 grid gap-3 border-t border-border pt-4 text-sm sm:grid-cols-2">
                <div>
                  <dt className="micro-label text-fg-muted">Tenant</dt>
                  <dd className="break-all text-fg">{guide.data.tenantId}</dd>
                </div>
                <div>
                  <dt className="micro-label text-fg-muted">Audience</dt>
                  <dd className="break-all text-fg">{guide.data.audience}</dd>
                </div>
              </dl>
              <div className="mt-4 border-t border-border pt-4">
                <p className="mb-2 text-sm font-semibold text-fg">App roles this API accepts</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-fg-muted">
                      <th className="py-1 pr-4 font-normal">Role value</th>
                      <th className="py-1 font-normal">What it grants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guide.data.roles.map((role) => (
                      <tr key={role.value} className="border-t border-border/60 align-top">
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <code>{role.value}</code>
                        </td>
                        <td className="py-2 text-fg-muted">{role.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </Dialog>
      ) : null}
    </div>
  )
}

// --- keys ---------------------------------------------------------------------------------

function HowKeysWork() {
  return (
    <section className="mb-8">
      <SectionHeader>How API keys work</SectionHeader>
      <ul className="card space-y-2 p-4 text-sm text-fg-muted">
        <li>
          A key acts <strong className="text-fg">as you</strong>, narrowed by its scopes. It can
          never do more than you can, and it stops working if your account does.
        </li>
        <li>
          Keys <strong className="text-fg">cannot create or revoke keys</strong>. That needs you,
          signed in, here.
        </li>
        <li>
          Keys are <strong className="text-fg">shown once</strong>. Only a hash is stored, so nobody
          — not an administrator, not the database — can recover one afterwards.
        </li>
        <li>Revoking a key takes effect on the very next request.</li>
        <li>You are emailed whenever a key on your account is created or revoked.</li>
      </ul>
      <p className="mb-2 mt-4 text-sm font-semibold text-fg">Keeping one safe</p>
      <ul className="card space-y-2 p-4 text-sm text-fg-muted">
        <li>
          <strong className="text-fg">Keep it out of the repository.</strong> Shell profile, CI
          secret store, or a <code>.env</code> in <code>.gitignore</code> — never a committed file,
          and never a chat message you would not want stored.
        </li>
        <li>
          <strong className="text-fg">Give it the shortest life that does the job.</strong> Trying
          something out? Make it a 1-day key. It expires on its own.
        </li>
        <li>
          <strong className="text-fg">Least scope.</strong> Reports and Power BI need only{' '}
          <code>:read</code> scopes. Reach for <code>*</code> only when you genuinely need
          everything.
        </li>
        <li>
          <strong className="text-fg">One key per place it lives</strong>, named after that place,
          so revoking one thing does not break the others.
        </li>
        <li>
          <strong className="text-fg">Leave the corporate-network pin on</strong> unless the caller
          genuinely runs elsewhere — Power BI&rsquo;s cloud refresh does.
        </li>
        <li>
          Revoke keys you have stopped using — the table below shows <em>Last used</em>. If a
          &ldquo;key created&rdquo; email arrives that you did not expect, revoke it and tell IT: it
          means someone has your session.
        </li>
      </ul>
    </section>
  )
}

function CreateKey({ base }: { base: string }) {
  const { data: scopes } = useScopes()
  const create = useCreateKey()
  const [name, setName] = useState('')
  const [chosen, setChosen] = useState<string[] | null>(null)
  const [days, setDays] = useState(30)
  const [custom, setCustom] = useState('')
  const [network, setNetwork] = useState<'corporate' | 'anywhere' | 'custom'>('corporate')
  const [addresses, setAddresses] = useState('')
  const [created, setCreated] = useState<ApiKeyCreated | null>(null)

  const selected = chosen ?? scopes?.defaultScopes ?? []
  const toggle = (code: string) =>
    setChosen((current) => {
      const list = current ?? scopes?.defaultScopes ?? []
      return list.includes(code) ? list.filter((c) => c !== code) : [...list, code]
    })

  const expiry = days === -1 ? Number(custom) : days
  const valid =
    name.trim().length > 0 &&
    selected.length > 0 &&
    Number.isInteger(expiry) &&
    expiry >= 1 &&
    expiry <= (scopes?.maxDays ?? 365) &&
    (network !== 'custom' || addresses.trim().length > 0)

  const reads = scopes?.scopes.filter((s) => !s.writes) ?? []
  const writes = scopes?.scopes.filter((s) => s.writes && s.code !== '*') ?? []
  const full = scopes?.scopes.find((s) => s.code === '*')

  return (
    <section className="mb-8">
      <SectionHeader>Create a key</SectionHeader>
      <div className="card space-y-5 p-5">
        <p className="text-sm text-fg-muted">
          For a script, a Power BI report, a pipeline or an integration that has to keep working.
          For a one-off ask to Claude, use <em>Start a Claude session</em> above instead.
        </p>
        <Field label="Name" hint="Something you will recognise in six months — where the key lives.">
          <input
            className={inputClass}
            value={name}
            maxLength={256}
            placeholder="Power BI — portfolio report"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <div>
          <span className="micro-label text-fg-muted">Scopes</span>
          <p className="mb-2 text-xs text-fg-muted">
            Grant the least a job needs. A key with only read scopes can never change anything.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              { title: 'Read', items: reads },
              { title: 'Write', items: writes },
            ].map((group) => (
              <div key={group.title}>
                <p className="mb-1 text-xs font-semibold text-fg">{group.title}</p>
                {group.items.map((scope) => (
                  <label key={scope.code} className="flex items-start gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selected.includes(scope.code)}
                      onChange={() => toggle(scope.code)}
                    />
                    <span>
                      <code className="text-fg">{scope.code}</code>{' '}
                      <span className="text-fg-muted">— {scope.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          {full ? (
            <label className="mt-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.includes('*')}
                onChange={() => toggle('*')}
              />
              <span>
                <code className="text-fg">*</code> <span className="text-fg-muted">— {full.label}</span>
              </span>
            </label>
          ) : null}
          {selected.includes('*') ? (
            <p className="mt-2 flex items-start gap-2 text-sm" style={{ color: 'var(--status-amber)' }}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
              Full access grants everything you can do, including deleting and administration if you
              hold them. Prefer specific scopes unless you genuinely need all of them.
            </p>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Expires" hint="There is no never-expiring option, on purpose.">
            <div className="flex gap-2">
              <select
                className={inputClass}
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
              >
                {(scopes?.expiryChoicesDays ?? [1, 7, 30, 90, 180, 365]).map((d) => (
                  <option key={d} value={d}>
                    {d === 1 ? '1 day' : d === 365 ? '1 year' : `${d} days`}
                  </option>
                ))}
                <option value={-1}>Custom</option>
              </select>
              {days === -1 ? (
                <input
                  className={inputClass}
                  inputMode="numeric"
                  placeholder={`1–${scopes?.maxDays ?? 365} days`}
                  value={custom}
                  onChange={(event) => setCustom(event.target.value)}
                />
              ) : null}
            </div>
          </Field>
          <Field label="Usable from">
            <div className="space-y-1 text-sm">
              {(
                [
                  ['corporate', 'The corporate network (recommended)'],
                  ['anywhere', 'Anywhere — needed for Power BI cloud refresh'],
                  ['custom', 'Specific addresses'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="network"
                    checked={network === value}
                    onChange={() => setNetwork(value)}
                  />
                  <span style={value === 'anywhere' ? { color: 'var(--status-amber)' } : undefined}>
                    {label}
                  </span>
                </label>
              ))}
              {network === 'custom' ? (
                <input
                  className={inputClass}
                  placeholder="203.0.113.7, 198.51.100.0/24"
                  value={addresses}
                  onChange={(event) => setAddresses(event.target.value)}
                />
              ) : null}
            </div>
          </Field>
        </div>
        {network === 'anywhere' ? (
          <p className="text-xs" style={{ color: 'var(--status-amber)' }}>
            API keys have no MFA behind them. A key usable from anywhere is shown in amber
            everywhere it appears; keep its scopes to reading.
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            disabled={!valid || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  scopes: selected,
                  expiresInDays: expiry,
                  network,
                  allowedIps: addresses
                    .split(/[\s,]+/)
                    .map((a) => a.trim())
                    .filter(Boolean),
                },
                {
                  onSuccess: (key) => {
                    setCreated(key)
                    setName('')
                  },
                },
              )
            }
          >
            <KeyRound size={14} aria-hidden /> {create.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </div>
        {create.error ? <ErrorState error={create.error} /> : null}
      </div>
      {created ? <Reveal created={created} base={base} onClose={() => setCreated(null)} /> : null}
    </section>
  )
}

function Reveal({
  created,
  base,
  onClose,
}: {
  created: ApiKeyCreated
  base: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const snippets = examples(base, created.token)
  const close = () => (copied || confirming ? onClose() : setConfirming(true))
  return (
    <Dialog label="Your new API key" onClose={close}>
      <h2 className="mb-1 font-heading text-2xl text-fg">Your new key — {created.name}</h2>
      <p className="mb-4 text-sm" style={{ color: 'var(--status-amber)' }}>
        This is the only time the key is shown. Only a hash is stored, so nobody can recover it
        afterwards. If you lose it, revoke it and create another.
      </p>
      <div className="mb-3 flex items-center gap-2">
        <code className="card flex-1 break-all p-3 text-xs text-fg">{created.token}</code>
        <Button
          variant="primary"
          onClick={() => {
            void navigator.clipboard.writeText(created.token).then(() => {
              setCopied(true)
              setConfirming(false)
            })
          }}
        >
          <Copy size={14} aria-hidden /> {copied ? 'Copied' : 'Copy key'}
        </Button>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <span>Scopes:</span>
        {created.scopes.map((scope) => (
          <Pill key={scope}>{scope}</Pill>
        ))}
        <span>· expires {when(created.expiresAt)}</span>
        <span>· usable from {created.network === 'anywhere' ? 'anywhere' : created.network === 'corporate' ? 'the corporate network' : created.allowedIps.join(', ')}</span>
      </div>
      <div className="space-y-3">
        <Snippet label="Put it where your shell will find it and git will not" code={snippets.env} />
        <Snippet label="curl" code={snippets.curl} />
        <Snippet label="Python" code={snippets.python} />
        <Snippet label="Power BI / Excel (Power Query)" code={snippets.powerbi} />
      </div>
      <div className="mt-4 flex items-center justify-end gap-3">
        {confirming ? (
          <span className="text-sm" style={{ color: 'var(--status-amber)' }}>
            You have not copied it. Close anyway?
          </span>
        ) : null}
        <Button onClick={close}>{confirming ? 'Close anyway' : 'Done'}</Button>
      </div>
    </Dialog>
  )
}

function KeyTable({ keys, any }: { keys: ApiKey[]; any: boolean }) {
  const revoke = useRevokeKey()
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-fg-muted">
            <th className="px-4 py-2 font-normal">Name</th>
            {any ? <th className="px-4 py-2 font-normal">Owner</th> : null}
            <th className="px-4 py-2 font-normal">Key</th>
            <th className="px-4 py-2 font-normal">Scopes</th>
            <th className="px-4 py-2 font-normal">Usable from</th>
            <th className="px-4 py-2 font-normal">Expires</th>
            <th className="px-4 py-2 font-normal">Last used</th>
            <th className="px-4 py-2 font-normal" />
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={key.id} className="border-b border-border/60 align-top last:border-0">
              <td className="px-4 py-3 text-fg">
                {key.name}
                {key.status !== 'active' ? (
                  <div className="mt-1">
                    <Pill tone={key.status === 'revoked' ? 'red' : 'neutral'}>{key.status}</Pill>
                  </div>
                ) : null}
              </td>
              {any ? <td className="px-4 py-3 text-fg-muted">{key.owner}</td> : null}
              <td className="px-4 py-3">
                <code className="text-xs">{key.maskedToken}</code>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {key.scopes.map((scope) => (
                    <Pill key={scope}>{scope}</Pill>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {key.network === 'anywhere' ? (
                  <span style={{ color: 'var(--status-amber)' }}>anywhere</span>
                ) : key.network === 'corporate' ? (
                  'corporate network'
                ) : (
                  key.allowedIps.join(', ')
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-fg-muted">{when(key.expiresAt)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-fg-muted">
                {when(key.lastUsedAt)}
                {key.lastUsedIp ? <div className="text-xs">{key.lastUsedIp}</div> : null}
              </td>
              <td className="px-4 py-3 text-right">
                {key.status === 'active' ? (
                  <Button
                    variant="destructive"
                    disabled={revoke.isPending}
                    onClick={() => {
                      if (window.confirm(`Revoke "${key.name}"? It stops working immediately.`)) {
                        revoke.mutate({ id: key.id, any })
                      }
                    }}
                  >
                    Revoke
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {revoke.error ? <ErrorState error={revoke.error} /> : null}
    </div>
  )
}

function MyKeys() {
  const { data, isPending, error } = useMyKeys()
  return (
    <section className="mb-8">
      <SectionHeader>Your keys</SectionHeader>
      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}
      {data && data.length === 0 ? (
        <EmptyState title="No keys yet">Create one above when something needs to keep working.</EmptyState>
      ) : null}
      {data && data.length > 0 ? <KeyTable keys={data} any={false} /> : null}
    </section>
  )
}

function AllKeys() {
  const [open, setOpen] = useState(false)
  const { data, isPending, error } = useAllKeys(open)
  return (
    <section className="mb-8">
      <SectionHeader
        action={
          <Button onClick={() => setOpen((v) => !v)}>
            <ShieldCheck size={14} aria-hidden /> {open ? 'Hide' : 'Show'} every key
          </Button>
        }
      >
        Every key (administrators)
      </SectionHeader>
      {open && isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}
      {open && data ? (
        data.length === 0 ? (
          <EmptyState title="Nobody has a key">Keys people create appear here.</EmptyState>
        ) : (
          <KeyTable keys={data} any />
        )
      ) : null}
    </section>
  )
}

// --- reference ------------------------------------------------------------------------------

function Reference({
  base,
  docsPath,
  openapiPath,
  audience,
}: {
  base: string
  docsPath?: string | null
  openapiPath?: string | null
  audience?: string
}) {
  const link = (path: string, label: string) => (
    <a
      href={`${base}${path}`}
      target="_blank"
      rel="noreferrer noopener"
      className="underline"
      style={{ color: 'var(--status-info)' }}
    >
      {label} <ExternalLink size={11} aria-hidden className="inline" />
    </a>
  )
  const tokenSnippet = [
    'TOKEN=$(az account get-access-token \\',
    `  --resource "${audience ?? ''}" \\`,
    '  --query accessToken -o tsv)',
    '',
    `curl -s -H "Authorization: Bearer $TOKEN" "${base}/api/v1/me"`,
  ].join('\n')
  return (
    <section className="mb-8">
      <SectionHeader>Reference</SectionHeader>
      <dl className="card mb-4 grid gap-3 p-4 text-sm sm:grid-cols-2">
        <div>
          <dt className="micro-label text-fg-muted">API base URL</dt>
          <dd className="break-all text-fg">{base}/api/v1</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Key header</dt>
          <dd className="text-fg">
            <code>X-API-Key</code>
          </dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Interactive documentation</dt>
          <dd>{docsPath ? link(docsPath, 'Swagger') : <span className="text-fg-muted">Not published here</span>}</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">OpenAPI document</dt>
          <dd>{openapiPath ? link(openapiPath, openapiPath) : <span className="text-fg-muted">Not published here</span>}</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Briefing for assistants</dt>
          <dd>{link('/api/assistant.md', '/api/assistant.md')}</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Read feeds (BI, scripts)</dt>
          <dd className="text-fg">
            <code>/api/v1/feed/…</code> — projects, work-items, milestones, assignments,
            dependencies, sprints, initiatives, people, wigs, wig-measures, wig-entries,
            wig-commitments
          </dd>
        </div>
      </dl>
      <p className="mb-2 text-sm text-fg-muted">
        Signed in as yourself instead of with a key — an Entra token from the Azure CLI, valid
        for about an hour:
      </p>
      <Snippet code={tokenSnippet} />
    </section>
  )
}
